import { Box, Button, Stack, Typography, styled } from '@mui/material';
import { GREEN, MUT, MUT_DARK, MUT2 } from './tokens';

export const Foot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '24px',
  flexWrap: 'wrap',
  marginTop: compact ? '22px' : '20px',
  paddingTop: compact ? '18px' : '18px',
  borderTop: '1px solid #eaeff4',
  ...theme.applyStyles('dark', {
    borderTop: `1px solid ${theme.palette.divider}`,
  }),
  ...(compact && {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '16px',
  }),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '16px',
  },
}));

export const MathRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '22px',
  flexWrap: 'wrap',
  ...(compact && { justifyContent: 'space-between', gap: '10px' }),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'space-between',
    gap: '8px',
  },
}));

export const MathOp = styled('span')(({ theme }) => ({
  fontSize: '20px',
  color: '#c3cdd6',
  [theme.breakpoints.down('sm')]: { fontSize: '16px' },
}));

export const MathLabel = styled(Typography)(({ theme }) => ({
  fontSize: '11.5px',
  fontWeight: 600,
  letterSpacing: '.7px',
  lineHeight: 1.3,
  color: MUT2,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  ...theme.applyStyles('dark', { color: theme.palette.text.secondary }),
  [theme.breakpoints.down('sm')]: { fontSize: '10px', letterSpacing: '.4px' },
}));

export const MathValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'emphasis',
})<{ emphasis?: 'green' | 'bold' }>(({ theme, emphasis }) => ({
  fontSize: '17px',
  fontWeight: 600,
  lineHeight: 1.2,
  marginTop: '2px',
  color: theme.palette.text.primary,
  ...(emphasis === 'green' && { color: GREEN }),
  ...(emphasis === 'bold' && { fontWeight: 700 }),
  [theme.breakpoints.down('sm')]: { fontSize: '14px' },
}));

export const CtaRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '14px',
  ...(compact && {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
    gap: '10px',
  }),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
    gap: '10px',
  },
}));

export const PayLine = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  fontSize: '13px',
  lineHeight: 1.3,
  color: MUT,
  ...theme.applyStyles('dark', { color: MUT_DARK }),
  ...(compact && { textAlign: 'center' }),
  [theme.breakpoints.down('sm')]: { textAlign: 'center' },
}));

export const AddToCartButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.common.white,
  fontSize: '15px',
  fontWeight: 600,
  padding: '14px 30px',
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  gap: '9px',
  // Pressable, in the guided-course idiom: a hard offset shadow rather than a
  // blur, collapsing on :active so the key visibly depresses.
  boxShadow: `0 5px 0 ${theme.palette.primary.dark}, 0 12px 22px -12px rgba(37,80,123,.6)`,
  transition: 'transform .12s ease, box-shadow .12s ease, filter .12s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    filter: 'brightness(1.06)',
    transform: 'translateY(-1px)',
    boxShadow: `0 6px 0 ${theme.palette.primary.dark}, 0 16px 26px -12px rgba(37,80,123,.65)`,
  },
  '&:active': {
    transform: 'translateY(5px)',
    boxShadow: `0 0 0 ${theme.palette.primary.dark}`,
  },
  ...(compact && { width: '100%', padding: '14px' }),
  [theme.breakpoints.down('sm')]: { width: '100%', fontSize: '14px' },
}));
